import React from 'react';
import { motion } from 'framer-motion';
import { Scroll, Shield } from 'lucide-react';
import SEOHelmet from '../components/SEOHelmet';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHelmet 
        title="Terms & Conditions"
        description="Terms and conditions for ticket purchases to the South African FinTech Awards 2025."
        keywords="terms and conditions, ticket terms, fintech awards terms"
      />

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <Scroll className="h-16 w-16" style={{ color: '#C1B3A3' }} />
            </div>
            <h1 className="text-4xl font-bold mb-6" style={{ color: '#C1B3A3' }}>
              Terms & Conditions
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ticket Purchase Terms for SA FinTech Awards 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-8">
              <div className="prose prose-lg max-w-none">
                <p className="font-semibold text-gray-900">
                  These Terms and Conditions ("Terms") govern the purchase and use of individual tickets to the South African Fintech Awards 2025, organised by TalentintheCloud (Pty) Ltd t/a titc.io ("titc", "the Organiser", "we", "us", "our"). By purchasing a ticket, the purchaser ("you", "your", "ticket holder") agrees to be bound by the following Terms:
                </p>

                <h2 className="text-2xl font-bold mt-8 mb-4">1. Ticket Purchase and Payment Terms</h2>
                <ul className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>The cost of an individual ticket is ZAR 9,750.00 excluding VAT.</li>
                  <li>VAT of 15%, subject to regulation changes, will be applied at the prevailing rate.</li>
                  <li>A pro forma invoice will be issued upon confirmation of your booking.</li>
                  <li>Full payment must be made within 48 hours of the date of the pro forma invoice.</li>
                  <li>An official tax invoice will only be issued upon receipt of payment in full or upon submission of valid proof of payment to titc.</li>
                  <li>titc reserves the right to cancel the booking if payment is not received within the stipulated timeframe.</li>
                  <li>No tickets will be issued and no access to the event will be granted until payment has been received in full and cleared.</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">2. Ticket Inclusions</h2>
                <div className="pl-6 space-y-4 text-gray-700">
                  <p>2.1. Each Ticket gives the ticket holder:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Admission for one individual to the SA Fintech Awards 2025 on 7 November 2025 in Cape Town.</li>
                    <li>A full gourmet dinner</li>
                    <li>Welcome drinks</li>
                    <li>Access to media coverage, photography, videography, and associated public relations materials, if photos or videos of the ticket holder are used by our media department</li>
                    <li>Inclusion in potential event media releases, subject to editorial discretion.</li>
                  </ul>
                  <p>2.2. Tickets are non-transferable without prior written consent from titc. Name changes must be submitted in writing and approved at least ten (10) days before the event. If such name changes are not submitted within the ten (10) day period, such name changes will not be accepted and the transferred ticket will not be correctly allocated.</p>
                  <p>2.3. Unallocated tickets will result in a blocked entry into the event without exception</p>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">3. Non-Refundable & No Cancellation Policy</h2>
                <ul className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>All ticket purchases are strictly non-refundable, including but not limited to non-attendance, scheduling conflicts, or change of personal circumstances.</li>
                  <li>No exchanges or cancellations will be permitted under any circumstances, except where required by law.</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">4. Event Amendments and Force Majeure</h2>
                <div className="pl-6 space-y-4 text-gray-700">
                  <p>4.1. While titc will make every effort to deliver the event as advertised, we reserve the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Make changes to the programme, speakers, venue, or catering without notice, and</li>
                    <li>Postpone or reschedule the event due to circumstances beyond our control, including but not limited to: natural disasters, government restrictions, pandemics, or other force majeure events.</li>
                  </ul>
                  <p>4.2. In the case of postponement or rescheduling, the ticket shall remain valid for the new date and no refunds will be issued.</p>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">5. Media Release and Consent</h2>
                <ul className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>By attending the SA Fintech Awards 2025, you grant titc and the affiliated sponsors the right to capture your image, likeness, and/or voice in photographs, videos, and other media recorded during the event.</li>
                  <li>You consent to titc and the affiliated sponsors to using such material for promotional, marketing, editorial, or any other lawful purpose in perpetuity, without compensation or further consent.</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">6. Code of Conduct and Right of Admission</h2>
                <ul className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>All attendees are expected to conduct themselves in a professional and respectful manner.</li>
                  <li>titc reserves the right of admission and may remove any attendee found to be disruptive, abusive, or in violation of event rules or applicable laws, without refund.</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">7. Liability Disclaimer</h2>
                <ul className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>Attendance is at your own risk. titc, its directors, employees, sponsors, and affiliates will not be liable for any loss, injury, damage, or expense suffered by any attendee in connection with the event.</li>
                  <li>You agree to indemnify and hold harmless titc, its directors, employees, sponsors, and affiliates against any claims arising from your conduct at or in connection with the event.</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">8. Governing Law and Jurisdiction</h2>
                <ul className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>These Terms are governed by the laws of the Republic of South Africa.</li>
                  <li>Any disputes arising from or related to these Terms or the event shall be subject to the exclusive jurisdiction of the South African courts.</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">9. Contact Information</h2>
                <p className="text-gray-700">
                  For any ticket-related queries, please contact titc via email at:{' '}
                  <a href="mailto:danielle@titc.io" className="text-indigo-600 hover:text-indigo-800">
                    danielle@titc.io
                  </a>
                </p>

                <div className="mt-12 p-6 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Shield className="h-6 w-6 text-indigo-600" />
                    <h3 className="text-lg font-semibold">Important Note</h3>
                  </div>
                  <p className="text-gray-600">
                    By proceeding with the purchase of a ticket, you confirm that you have read, understood, and agreed to these Terms and Conditions in full.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;